'use client'

import React from 'react'
import {
  Radar,
  Search,
  Bot,
  MessageSquare,
  ArrowDown,
  ArrowUp,
  Scan,
  FileText,
  Shield,
  BarChart3,
  Users,
  Workflow,
  AlertTriangle,
  CheckCircle,
  Zap,
  Target,
  Eye,
  RefreshCw,
} from 'lucide-react'

/**
 * AI Visibility Stack Diagram
 * Shows the 3-layer architecture: Radar (monitoring) → Vector (conversion) → Hive (orchestration)
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

export function VisibilityStackDiagram() {
  const radarTools = [
    { name: 'Crawl Checker', icon: Scan },
    { name: 'robots.txt', icon: Shield },
    { name: 'llms.txt', icon: FileText },
    { name: 'Readiness Score', icon: BarChart3 },
    { name: 'Citation Tracker', icon: MessageSquare },
    { name: 'Reddit Monitor', icon: Eye },
  ]

  const vectorDimensions = [
    'Intent signals',
    'Firmographic fit',
    'Engagement depth',
    'AI referral source',
  ]

  const hiveAgents = [
    {
      name: 'Monitoring',
      icon: Radar,
      role: 'Citations, rankings, bot access',
    },
    {
      name: 'Content',
      icon: FileText,
      role: 'Decay detection, structured data',
    },
    { name: 'Operations', icon: Workflow, role: 'Prioritization, escalation' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-8'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE AI VISIBILITY STACK
        </h3>
        <p className='text-sm text-muted-foreground'>
          Three layers working together: monitor, qualify, orchestrate
        </p>
      </div>

      {/* Layer 1: Radar */}
      <div className='mb-4'>
        <div className='bg-background border border-border/60 rounded-xl p-5 md:p-6'>
          <div className='flex items-center gap-3 mb-4'>
            <div
              className='w-10 h-10 rounded-lg flex items-center justify-center'
              style={{ backgroundColor: '#008dcc20' }}
            >
              <Radar className='w-5 h-5' style={{ color: '#008dcc' }} />
            </div>
            <div>
              <div className='font-bold text-foreground text-lg'>
                Layer 1: Discovery Monitoring
              </div>
              <div className='text-xs font-medium' style={{ color: '#008dcc' }}>
                RADAR
              </div>
            </div>
          </div>

          {/* 13 tools grid */}
          <div className='grid grid-cols-2 md:grid-cols-3 gap-2 mb-4'>
            {radarTools.map(tool => (
              <div
                key={tool.name}
                className='flex items-center gap-2 px-3 py-2 rounded-lg bg-muted'
              >
                <tool.icon
                  className='w-3.5 h-3.5 flex-shrink-0'
                  style={{ color: '#008dcc' }}
                />
                <span className='text-xs text-foreground/80'>{tool.name}</span>
              </div>
            ))}
          </div>

          {/* Output */}
          <div
            className='flex items-center gap-2 px-4 py-2.5 rounded-lg border'
            style={{
              backgroundColor: '#008dcc08',
              borderColor: '#008dcc30',
            }}
          >
            <AlertTriangle
              className='w-4 h-4 flex-shrink-0'
              style={{ color: '#008dcc' }}
            />
            <span className='text-sm text-foreground/80'>
              Cross-tool conflict detection + AI Readiness Score (0-100)
            </span>
          </div>
        </div>
      </div>

      {/* Arrow down */}
      <div className='flex justify-center my-2'>
        <div className='flex flex-col items-center'>
          <ArrowDown className='w-5 h-5 text-muted-foreground' />
          <span className='text-[10px] text-muted-foreground mt-0.5'>
            AI-referred traffic
          </span>
        </div>
      </div>

      {/* Layer 2: Vector */}
      <div className='mb-4'>
        <div className='bg-background border border-border/60 rounded-xl p-5 md:p-6'>
          <div className='flex items-center gap-3 mb-4'>
            <div
              className='w-10 h-10 rounded-lg flex items-center justify-center'
              style={{ backgroundColor: '#8b5cf620' }}
            >
              <Target className='w-5 h-5' style={{ color: '#8b5cf6' }} />
            </div>
            <div>
              <div className='font-bold text-foreground text-lg'>
                Layer 2: Conversion Intelligence
              </div>
              <div className='text-xs font-medium' style={{ color: '#8b5cf6' }}>
                VECTOR
              </div>
            </div>
          </div>

          {/* Scoring flow */}
          <div className='flex flex-col md:flex-row items-stretch gap-3'>
            <div className='flex-1 bg-muted rounded-lg p-3'>
              <div className='text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5'>
                <Users className='w-3.5 h-3.5' style={{ color: '#8b5cf6' }} />
                12-Dimension Scoring
              </div>
              <div className='space-y-1.5'>
                {vectorDimensions.map(dim => (
                  <div key={dim} className='flex items-center gap-2'>
                    <div
                      className='w-1.5 h-1.5 rounded-full flex-shrink-0'
                      style={{ backgroundColor: '#8b5cf6' }}
                    />
                    <span className='text-xs text-foreground/80'>{dim}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='flex items-center justify-center md:px-2'>
              <ArrowDown className='w-4 h-4 md:hidden text-muted-foreground' />
              <svg
                className='hidden md:block w-6 h-4 text-muted-foreground'
                viewBox='0 0 24 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M2 8h16m0 0l-4-4m4 4l-4 4' />
              </svg>
            </div>

            <div className='flex-1 bg-muted rounded-lg p-3'>
              <div className='text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5'>
                <Zap className='w-3.5 h-3.5' style={{ color: '#FDC304' }} />
                Output
              </div>
              <div className='space-y-1.5'>
                <div className='flex items-center gap-2'>
                  <CheckCircle
                    className='w-3.5 h-3.5 flex-shrink-0'
                    style={{ color: '#22c55e' }}
                  />
                  <span className='text-xs text-foreground/80'>
                    Qualified lead routed to sales
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <Search
                    className='w-3.5 h-3.5 flex-shrink-0'
                    style={{ color: '#008dcc' }}
                  />
                  <span className='text-xs text-foreground/80'>
                    AI referral source tagged
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <Bot
                    className='w-3.5 h-3.5 flex-shrink-0'
                    style={{ color: '#8b5cf6' }}
                  />
                  <span className='text-xs text-foreground/80'>
                    Context-rich handoff to team
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Arrow down */}
      <div className='flex justify-center my-2'>
        <div className='flex flex-col items-center'>
          <ArrowDown className='w-5 h-5 text-muted-foreground' />
          <span className='text-[10px] text-muted-foreground mt-0.5'>
            Operational signals
          </span>
        </div>
      </div>

      {/* Layer 3: Hive */}
      <div className='mb-4'>
        <div className='bg-background border border-border/60 rounded-xl p-5 md:p-6'>
          <div className='flex items-center gap-3 mb-4'>
            <div
              className='w-10 h-10 rounded-lg flex items-center justify-center'
              style={{ backgroundColor: '#FDC30420' }}
            >
              <Workflow className='w-5 h-5' style={{ color: '#FDC304' }} />
            </div>
            <div>
              <div className='font-bold text-foreground text-lg'>
                Layer 3: Operational Orchestration
              </div>
              <div className='text-xs font-medium' style={{ color: '#FDC304' }}>
                HIVE
              </div>
            </div>
          </div>

          {/* 3 agents */}
          <div className='grid md:grid-cols-3 gap-3'>
            {hiveAgents.map(agent => (
              <div key={agent.name} className='bg-muted rounded-lg p-3'>
                <div className='flex items-center gap-2 mb-1.5'>
                  <agent.icon
                    className='w-4 h-4 flex-shrink-0'
                    style={{ color: '#FDC304' }}
                  />
                  <span className='text-sm font-semibold text-foreground'>
                    {agent.name}
                  </span>
                </div>
                <p className='text-xs text-muted-foreground'>{agent.role}</p>
              </div>
            ))}
          </div>

          {/* Shared intelligence note */}
          <div
            className='mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg border'
            style={{
              backgroundColor: '#FDC30408',
              borderColor: '#FDC30430',
            }}
          >
            <Bot
              className='w-4 h-4 flex-shrink-0'
              style={{ color: '#FDC304' }}
            />
            <span className='text-xs text-foreground/80'>
              All agents share unified intelligence (powered by Vector)
            </span>
          </div>
        </div>
      </div>

      {/* Feedback loop */}
      <div className='flex justify-center mt-2'>
        <div
          className='flex items-center gap-2 px-4 py-2.5 rounded-full border'
          style={{
            backgroundColor: '#F90B8A08',
            borderColor: '#F90B8A30',
          }}
        >
          <RefreshCw className='w-4 h-4' style={{ color: '#F90B8A' }} />
          <span className='text-xs font-medium text-foreground/80'>
            Actions feed back into monitoring (continuous loop)
          </span>
          <ArrowUp className='w-4 h-4' style={{ color: '#F90B8A' }} />
        </div>
      </div>
    </div>
  )
}
